import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Zap, Send, Users, Smartphone, Mail, Shield, TrendingUp, Sparkles, ArrowRight, CheckCircle, Globe, Target } from 'lucide-react';

const FEATURES = [
  { icon: Send, title: 'Masowa wysylka SMS', desc: 'Wysylaj SMS przez telefony Android i modemy GSM. Bez posrednikow, bez limitow operatorow.' },
  { icon: Mail, title: 'Email marketing', desc: 'Profesjonalne kampanie email z personalizacja, trackingiem i automatycznym unsubscribe.' },
  { icon: Users, title: 'Zarzadzanie kontaktami', desc: 'Import CSV/XLSX, segmentacja, scoring aktywnosci, kategorie zainteresowwan.' },
  { icon: Globe, title: 'Zbieracz danych z WWW', desc: 'Automatycznie zbieraj emaile i telefony ze stron internetowych. Weryfikuj i importuj.' },
  { icon: Sparkles, title: 'Wsparcie AI', desc: 'Generowanie tresci SMS, klasyfikacja odpowiedzi, auto-blacklist, kategoryzacja kontaktow.' },
  { icon: Target, title: 'Tracking afiliacyjny', desc: 'Linki z trackingiem, sledzenie konwersji, automatyczne naliczanie prowizji.' },
  { icon: Shield, title: 'RODO / Compliance', desc: 'Czarna lista, opt-out, eksport i usuwanie danych, DNS checker (SPF/DKIM/DMARC).' },
  { icon: Smartphone, title: 'Bramki SMS', desc: 'Podlacz modemy GSM 32-portowe lub telefony Android. Monitoring, limity, heartbeat.' },
];

export default function HomePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_10%,hsl(189_70%_45%/0.12),transparent_55%),radial-gradient(700px_circle_at_80%_90%,hsl(206_70%_55%/0.08),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-4 text-primary border-primary/30">Program afiliacyjny 50/50</Badge>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Monetyzuj swoje <span className="text-primary">bazy kontaktow</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mt-6 leading-relaxed max-w-2xl">
              Masz baze adresow email lub numerow telefonow? <strong className="text-foreground">Zarabiaj na nich bez ponoszenia kosztow wysylki.</strong> Dolacz do programu afiliacyjnego Torr.pl i dziel zyski 50/50.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/register">
                <Button size="lg" className="text-base px-8" data-testid="hero-cta-register">Zacznij zarabiac <ArrowRight className="h-4 w-4 ml-2" /></Button>
              </Link>
              <Link to="/kontakt">
                <Button variant="outline" size="lg" className="text-base px-8">Dowiedz sie wiecej</Button>
              </Link>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-[hsl(var(--success))]" />Darmowa rejestracja</div>
              <div className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-[hsl(var(--success))]" />Bez kosztow wysylki</div>
              <div className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-[hsl(var(--success))]" />Zysk 50/50</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl font-semibold tracking-tight">Jak to dziala?</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Trzy proste kroki do zarabiania na swoich bazach kontaktow</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { step: '1', title: 'Zarejestruj sie', desc: 'Utworz darmowe konto afilianta. Nie wymagamy zadnych oplat ani zobowiazan.' },
            { step: '2', title: 'Wgraj swoja baze', desc: 'Importuj kontakty z CSV lub uzyj naszego zbieracza emaili i telefonow z WWW.' },
            { step: '3', title: 'Zarabiaj 50%', desc: 'My wysylamy kampanie SMS i email. Ty zarabiasz 50% zysku z kazdej konwersji.' },
          ].map(s => (
            <Card key={s.step} className="border-border/50 bg-card/80 text-center">
              <CardContent className="pt-8 pb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="font-heading text-xl font-bold text-primary">{s.step}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Affiliate program */}
      <section className="bg-card/50 border-y border-border/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">Propozycja wspolpracy</Badge>
              <h2 className="font-heading text-3xl font-semibold tracking-tight">Program afiliacyjny <span className="text-primary">50/50</span></h2>
              <p className="text-muted-foreground mt-4 leading-relaxed">
                Zapraszamy wszystkich posiadaczy baz emaili i numerow telefonow do wspolpracy. Nie musisz nic placic — koszty wysylki SMS i email sa po naszej stronie.
              </p>
              <div className="space-y-4 mt-6">
                {[
                  { title: 'Zero kosztow wysylki', desc: 'Nie ponosisz zadnych kosztow za SMS-y ani emaile. Infrastruktura po naszej stronie.' },
                  { title: 'Uczciwy podzial zyskow', desc: '50% kazdej prowizji z konwersji trafia do Ciebie. Przejrzyste rozliczenia w panelu.' },
                  { title: 'Pelna kontrola', desc: 'Widzisz statystyki klikniec, konwersji i zarobkow w czasie rzeczywistym.' },
                  { title: 'Wyplata na zadanie', desc: 'Zloz wniosek o wyplate gdy chcesz. Szybkie przelewy na konto.' },
                ].map((f, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="h-5 w-5 text-[hsl(var(--success))] shrink-0 mt-0.5" />
                    <div><p className="text-sm font-medium">{f.title}</p><p className="text-xs text-muted-foreground">{f.desc}</p></div>
                  </div>
                ))}
              </div>
              <Link to="/register">
                <Button className="mt-8" size="lg">Dolacz do programu <ArrowRight className="h-4 w-4 ml-2" /></Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '50%', label: 'Twoja prowizja', color: 'text-[hsl(var(--success))]' },
                { value: '0 zl', label: 'Koszt wysylki', color: 'text-primary' },
                { value: '24/7', label: 'Panel afilianta', color: 'text-[hsl(var(--info))]' },
                { value: 'AI', label: 'Wsparcie tresci', color: 'text-[hsl(var(--chart-3))]' },
              ].map((s, i) => (
                <Card key={i} className="border-border/50 bg-card/80">
                  <CardContent className="pt-6 pb-5 text-center">
                    <p className={`font-heading text-3xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl font-semibold tracking-tight">Mozliwosci platformy</h2>
          <p className="text-muted-foreground mt-3">Kompletne narzedzie do komunikacji marketingowej</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <Card key={i} className="border-border/50 bg-card/80 hover:shadow-lg transition-shadow duration-200">
              <CardContent className="pt-5 pb-4 px-4">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <f.icon className="h-4.5 w-4.5 text-primary" />
                </div>
                <h3 className="font-heading text-sm font-semibold mb-1">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-12 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold">Masz baze kontaktow? Zacznij zarabiac juz dzis!</h2>
            <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Dolacz do grona wydawcow Torr.pl. Darmowa rejestracja, brak zobowiazan, uczciwy podzial zyskow 50/50.</p>
            <div className="flex justify-center gap-3 mt-6">
              <Link to="/register"><Button size="lg">Zarejestruj sie za darmo</Button></Link>
              <Link to="/blog"><Button variant="outline" size="lg">Czytaj blog</Button></Link>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
